import './style/index.less';
import React, { forwardRef, ReactNode } from 'react';
import useScreenDesign, { screenType } from '@/Core/utils/hooks/useScreenDesign';
import classNames from 'classnames';

type NativeButtonProps = Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'type'>;
type ButtonVisualType =
  | 'default'
  | 'primary'
  | 'secondary'
  | 'dashed'
  | 'outline'
  | 'text';
type ButtonSize = 'mini' | 'small' | 'default' | 'large';
type ButtonShape = 'circle' | 'round' | 'square';
type ButtonStatus = 'default' | 'success' | 'warning' | 'danger';

export type ButtonUIProps = NativeButtonProps & {
  buttonColor?: 'link-6' | 'secondary' | 'white' | 'blue-1';
  type?: ButtonVisualType;
  htmlType?: 'button' | 'submit' | 'reset';
  loading?: boolean;
  long?: boolean;
  icon?: ReactNode;
  size?: ButtonSize;
  shape?: ButtonShape;
  status?: ButtonStatus;
  loadingFixedWidth?: boolean;
};

const ButtonUI = forwardRef<HTMLButtonElement, ButtonUIProps>((props, ref) => {
  //继承属性，自定义逻辑
  const {
    buttonColor,
    children,
    className,
    disabled,
    htmlType = 'button',
    icon,
    loading,
    loadingFixedWidth: _loadingFixedWidth,
    long,
    shape,
    size,
    status,
    style,
    type = 'default',
    ...other
  } = props;
  const mobile = useScreenDesign(screenType.mobile);
  const prefix = 'buttonui';
  const variantClassName = buttonColor ? `${prefix}-${buttonColor}` : undefined;
  const resolvedSize = size || (mobile ? 'small' : 'default');
  const buttonTypeClassName = `${prefix}-type-${type}`;
  const isDisabled = Boolean(disabled || loading);
  const shapeClassName = shape ? `${prefix}-shape-${shape}` : undefined;
  const statusClassName = status ? `${prefix}-status-${status}` : undefined;

  return (
    <button
      ref={ref}
      type={htmlType}
      disabled={isDisabled}
      aria-busy={loading || undefined}
      style={style}
      {...other}
      className={classNames([
        `buttonui-size-${resolvedSize}`,
        buttonTypeClassName,
        shapeClassName,
        statusClassName,
        {
          'buttonui-disabled': isDisabled,
          'buttonui-loading': loading,
          'buttonui-long': long,
        },
        prefix,
        className,
        variantClassName,
      ])}
    >
      {loading && (
        <span
          className="buttonui-loading-icon"
          aria-hidden="true"
        />
      )}
      {icon && <span className="buttonui-icon">{icon}</span>}
      {children !== undefined && children !== null && <span>{children}</span>}
    </button>
  );
});

ButtonUI.displayName = 'ButtonUI';

export default ButtonUI;
